import * as React from "react"
import * as ToastPrimitives from "@radix-ui/react-toast"
import { cva, type VariantProps } from "class-variance-authority"
import { AlertCircle, AlertTriangle, Check, Info, X } from "lucide-react"

import { cn } from "@/lib/utils"

const ToastProvider = ToastPrimitives.Provider

function ToastViewport({
  className,
  ...props
}: React.ComponentProps<typeof ToastPrimitives.Viewport>) {
  return (
    <ToastPrimitives.Viewport
      data-slot="toast-viewport"
      className={cn(
        // Toasts are the topmost layer — above stacked dialogs (200 + 20×depth,
        // see DialogActionHost), the command palette (300) and the AI search
        // backdrop (1000), so validation toasts fired from inside a dialog
        // chain stay visible.
        "fixed top-0 z-[1100] flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]",
        className
      )}
      {...props}
    />
  )
}

// Toast variants — each carries a left accent strip + matching icon bubble so
// the user can scan multiple toasts at once and immediately tell success from
// error from warning. The wrapper just sets the strip's background colour;
// the icon bubble + colour comes from `data-slot="toast-icon"` styled in
// _toast.scss (see the matching SCSS layer for the strip + icon classes).
const toastVariants = cva(
  "toast-card",
  {
    variants: {
      variant: {
        default: "toast-card--default",
        success: "toast-card--success",
        destructive: "toast-card--destructive",
        warning: "toast-card--warning",
        info: "toast-card--info",
      },
    },
    defaultVariants: { variant: "default" },
  }
)

function Toast({
  className,
  variant,
  ...props
}: React.ComponentProps<typeof ToastPrimitives.Root> &
  VariantProps<typeof toastVariants>) {
  return (
    <ToastPrimitives.Root
      data-slot="toast"
      data-variant={variant ?? "default"}
      className={cn(toastVariants({ variant }), className)}
      {...props}
    />
  )
}

// Per-variant icon. Rendered by the Toaster component based on the active
// variant — the Toast wrapper just paints the strip + colour scheme.
function ToastIcon({
  variant,
}: {
  variant?: VariantProps<typeof toastVariants>["variant"]
}) {
  if (variant === "default" || !variant) return null
  const Icon =
    variant === "success" ? Check
    : variant === "destructive" ? AlertCircle
    : variant === "warning" ? AlertTriangle
    : variant === "info" ? Info
    : null
  if (!Icon) return null
  return (
    <span className="toast-card__icon" aria-hidden="true">
      <Icon size={14} strokeWidth={2.4} />
    </span>
  )
}

function ToastAction({
  className,
  ...props
}: React.ComponentProps<typeof ToastPrimitives.Action>) {
  return (
    <ToastPrimitives.Action
      data-slot="toast-action"
      className={cn("toast-card__action", className)}
      {...props}
    />
  )
}

function ToastClose({
  className,
  ...props
}: React.ComponentProps<typeof ToastPrimitives.Close>) {
  return (
    <ToastPrimitives.Close
      data-slot="toast-close"
      className={cn("toast-card__close", className)}
      {...props}
    >
      <X size={14} strokeWidth={2} />
    </ToastPrimitives.Close>
  )
}

function ToastTitle({
  className,
  ...props
}: React.ComponentProps<typeof ToastPrimitives.Title>) {
  return (
    <ToastPrimitives.Title
      data-slot="toast-title"
      className={cn("toast-card__title", className)}
      {...props}
    />
  )
}

function ToastDescription({
  className,
  ...props
}: React.ComponentProps<typeof ToastPrimitives.Description>) {
  return (
    <ToastPrimitives.Description
      data-slot="toast-description"
      className={cn("toast-card__desc", className)}
      {...props}
    />
  )
}

type ToastPropsType = React.ComponentProps<typeof Toast>
type ToastActionElement = React.ReactElement<typeof ToastAction>
type ToastVariantType = NonNullable<VariantProps<typeof toastVariants>["variant"]>

export {
  type ToastPropsType as ToastProps,
  type ToastActionElement,
  type ToastVariantType as ToastVariant,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastIcon,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
}
